import type {
  AssistantConversationSummary,
  AssistantJobStatus,
  AssistantUsage,
} from "@/base/farmhq-api"
import type { RootState } from "./root.reducer"

import {
  askAssistantAsync,
  getAssistantConversationAsync,
  getAssistantJobAsync,
  getAssistantUsageAsync,
  listAssistantConversationsAsync,
} from "@/base/farmhq-api"
import { createSlice } from "@reduxjs/toolkit"

import { signOutAsync } from "./auth.reducer"

export interface AssistantMessage {
  role: "user" | "assistant"
  text: string
}

interface AssistantState {
  conversationId: number | null
  messages: AssistantMessage[]
  activeJobId: number | null
  jobStatus: AssistantJobStatus | null
  usage: AssistantUsage | null
  conversations: AssistantConversationSummary[]
  isSending: boolean
  isUnavailable: boolean
  isBudgetExceeded: boolean
}

const initialState: AssistantState = {
  conversationId: null,
  messages: [],
  activeJobId: null,
  jobStatus: null,
  usage: null,
  conversations: [],
  isSending: false,
  isUnavailable: false,
  isBudgetExceeded: false,
}

const slice = createSlice({
  name: "assistant",
  initialState,
  reducers: {
    startNewConversation(state) {
      state.conversationId = null
      state.messages = []
      state.activeJobId = null
      state.jobStatus = null
      state.isSending = false
      state.isBudgetExceeded = false
    },
    selectConversation(state, action: { payload: number }) {
      state.conversationId = action.payload
      state.messages = []
      state.activeJobId = null
      state.jobStatus = null
      state.isSending = false
      state.isBudgetExceeded = false
    },
    appendUserMessage(state, action: { payload: string }) {
      state.messages.push({ role: "user", text: action.payload })
      state.isSending = true
      state.isBudgetExceeded = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(askAssistantAsync.fulfilled, (state, action) => {
      const payload = action.payload
      if (payload.status === "OK") {
        state.activeJobId = payload.jobId
        state.jobStatus = payload.jobStatus
        state.conversationId = payload.conversationId
      } else if (payload.status === "BUDGET_EXCEEDED") {
        state.isSending = false
        state.isBudgetExceeded = true
        state.usage = payload.usage
      } else if (payload.status === "UNAVAILABLE") {
        state.isSending = false
        state.isUnavailable = true
      }
    })
    builder.addCase(askAssistantAsync.rejected, (state) => {
      state.isSending = false
    })
    builder.addCase(getAssistantJobAsync.fulfilled, (state, action) => {
      const job = action.payload
      state.jobStatus = job.status
      if (job.status === "SUCCESS") {
        state.messages.push({ role: "assistant", text: job.resultText })
        state.isSending = false
        state.activeJobId = null
      } else if (job.status === "FAILED") {
        state.messages.push({
          role: "assistant",
          text: job.error || "Something went wrong.",
        })
        state.isSending = false
        state.activeJobId = null
      }
    })
    builder.addCase(getAssistantUsageAsync.fulfilled, (state, action) => {
      state.usage = action.payload
    })
    builder.addCase(
      listAssistantConversationsAsync.fulfilled,
      (state, action) => {
        state.conversations = action.payload.items
      },
    )
    builder.addCase(
      getAssistantConversationAsync.fulfilled,
      (state, action) => {
        state.conversationId = action.payload.id
        state.messages = action.payload.messages
      },
    )
    builder.addCase(signOutAsync.fulfilled, () => initialState)
  },
})

export const { startNewConversation, selectConversation, appendUserMessage } =
  slice.actions

export default slice.reducer

// Selectors
export const getAssistantMessages = (state: RootState) =>
  state.assistant.messages
export const getAssistantConversationId = (state: RootState) =>
  state.assistant.conversationId
export const getAssistantIsSending = (state: RootState) =>
  state.assistant.isSending
export const getAssistantActiveJobId = (state: RootState) =>
  state.assistant.activeJobId
export const getAssistantUsage = (state: RootState) => state.assistant.usage
export const getAssistantConversations = (state: RootState) =>
  state.assistant.conversations
export const getAssistantIsUnavailable = (state: RootState) =>
  state.assistant.isUnavailable
export const getAssistantIsBudgetExceeded = (state: RootState) =>
  state.assistant.isBudgetExceeded
